/**
 * People directory (§2.6, screenshots/18; resident 21 and office 22 variants). Filters, type tabs with counts,
 * the table (Person · Type · Ward · Appearances · Policy areas · Leaning (AI estimate) · Last spoke) and
 * "Load more". Rows select their own person by id, so a tag or selection change re-renders one row.
 * ⌘/Ctrl-click or the row checkbox selects people: two → Compare (screenshots/46); offices tag or export the
 * selection (card 9).
 */
import { memo, useEffect, useRef, useState } from "react";
import { ArrowDownUp, ChevronDown, Download, Info, Landmark, MapPin, Search, Sparkles, StickyNote, Tag, X } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { List, ListRow, RowCell, RowMain, RowMeta, RowSub, RowTitle } from "../components/shared/List";
import { useShallow } from "zustand/react/shallow";
import { recipes, prefersReducedMotion, reducedFallback } from "../motion";
import { PAGE, peopleStore, usePeople } from "./peopleStore";
import { AreaChip, PeekName, PersonImg, TypeChip, Watch, mdy } from "./atoms";
import { LeaningMeter } from "./LeaningMeter";
import { NoResults } from "../components/shared/States";
import { compareHref } from "./compareLogic";
import type { DirectoryQuery, TeamTag, TypeTab } from "./model";
import { onTablistKey } from "../hooks/useTabs";

const TABS: { id: TypeTab; label: string }[] = [
  { id: "all", label: "All" }, { id: "official", label: "Officials" }, { id: "resident", label: "Residents" },
  { id: "lobbyist", label: "Lobbyists" }, { id: "org_rep", label: "Org reps" },
];
const SORTS: { id: DirectoryQuery["sort"]; label: string }[] = [{ id: "active", label: "Most active" }, { id: "recent", label: "Last spoke" }, { id: "name", label: "Name" }];
const LEANINGS = [["progressive", "Progressive"], ["moderate", "Moderate"], ["conservative", "Conservative"]] as const;

/** The design's filter pill ("Ward: Any ⌄") opening the shared Dropdown list. */
function FilterSelect<T extends string>({ icon, label, value, options, onChange, active }: {
  icon: React.ReactNode; label: string; value: T; options: readonly (readonly [T, string])[]; onChange: (v: T) => void; active?: boolean;
}) {
  const shown = options.find(([v]) => v === value)?.[1] ?? "Any";
  return (
    <Dropdown className="p-filter" data={{ "data-active": active || undefined }} label={label || "Sort"} value={value} onChange={onChange} icon={icon}
      display={label ? `${label}: ${shown}` : shown} chevron={<ChevronDown size={14} aria-hidden />} options={options.map(([v, l]) => ({ value: v, label: l }))} />
  );
}

export function SearchBox({ placeholder, value, onChange, width }: { placeholder: string; value: string; onChange: (v: string) => void; width?: number }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // ⌘K here means this search box (as Find in document does on the Documents tab): captured first, so the
    // command palette doesn't also open and later hand focus back to this box.
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); e.stopPropagation(); ref.current?.focus(); } };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, []);
  return (
    <label className="p-search" style={width ? { width, flex: "none" } : undefined}>
      <Search size={15} aria-hidden />
      <input ref={ref} placeholder={placeholder} aria-label={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <kbd>⌘K</kbd>
    </label>
  );
}

export function DirectoryFilters({ wardActive }: { wardActive?: boolean }) {
  const { query, areas } = usePeople(useShallow((s) => ({ query: s.query, areas: s.areas })));
  const [q, setQ] = useState(query.q);
  useEffect(() => { const t = setTimeout(() => q !== peopleStore.getState().query.q && peopleStore.getState().setQuery({ q }), 200); return () => clearTimeout(t); }, [q]);
  const set = peopleStore.getState().setQuery;
  const wards = [["", "Any"] as const, ...Array.from({ length: 50 }, (_, i) => [String(i + 1), String(i + 1)] as const)];
  return (
    <div className="p-filters">
      <SearchBox placeholder="Search people, wards, or organizations" value={q} onChange={setQ} />
      <FilterSelect icon={<MapPin size={15} aria-hidden />} label="Ward" value={query.ward == null ? "" : String(query.ward)} options={wards}
        active={wardActive && query.ward != null} onChange={(v) => set({ ward: v ? Number(v) : null })} />
      <FilterSelect icon={<Landmark size={15} aria-hidden />} label="Policy area" value={query.area ?? ""} options={[["", "Any"], ...areas.map((a) => [a, a] as const)]}
        onChange={(v) => set({ area: v || null })} />
      <FilterSelect icon={<Sparkles size={15} aria-hidden />} label="Leaning" value={query.leaning ?? ""} options={[["", "Any"], ...LEANINGS]}
        onChange={(v) => set({ leaning: (v || null) as DirectoryQuery["leaning"] })} />
      <FilterSelect icon={<ArrowDownUp size={15} aria-hidden />} label="" value={query.sort} options={SORTS.map((s) => [s.id, s.label] as const)} onChange={(v) => set({ sort: v })} />
    </div>
  );
}

export function TypeTabs() {
  const { type, counts } = usePeople(useShallow((s) => ({ type: s.query.type, counts: s.counts })));
  return (
    <div className="p-typetabs" role="tablist" aria-label="Person type" onKeyDown={onTablistKey}>
      {TABS.filter((t) => t.id === "all" || counts[t.id] > 0).map((t) => (
        <button key={t.id} role="tab" aria-selected={type === t.id} onClick={() => peopleStore.getState().setQuery({ type: t.id })}>
          {t.label}<span>{counts[t.id].toLocaleString("en-US")}</span>
        </button>
      ))}
    </div>
  );
}

const TAG_TONE: Record<TeamTag["tone"], string> = { neutral: "neutral", positive: "positive", negative: "negative", warning: "warning", person: "person" };

const Row = memo(function Row({ id, index, office }: { id: string; index: number; office: boolean }) {
  const p = usePeople((s) => s.byId[id]);
  const selected = usePeople((s) => s.selected.includes(id));
  const anySelected = usePeople((s) => s.selected.length > 0);
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // List entrance (motion #2): 8px rise, 30 ms stagger, at most 8 rows staggered.
    const r = recipes.listEntrance;
    if (index >= r.maxStaggered) return;
    el.current?.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: `translateY(${r.from.y}px)` }, { opacity: 1, transform: "none" }],
      { duration: prefersReducedMotion() ? reducedFallback.duration : r.duration, easing: r.easing, delay: prefersReducedMotion() ? 0 : index * r.stagger, fill: "backwards" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return null;
  const toggle = () => peopleStore.getState().toggleSelect(id, !office);
  const onClick = (e: React.MouseEvent) => { if (e.metaKey || e.ctrlKey) { e.preventDefault(); toggle(); } };
  return (
    <ListRow as="div" className="p-row" role="row" selected={selected} data-office={office || undefined} data-frame={p.image.kind !== "portrait" || undefined}
      ref={el} onClick={onClick}>
      <RowMain min={180} className="p-cell p-cell--person">
        <span role="cell" className="p-cell__in">
          <input type="checkbox" className="p-check" data-show={anySelected || undefined} checked={selected} onChange={toggle} aria-label={`Select ${p.name}`} />
          <PersonImg image={p.image} name={p.name} />
          <span className="p-name">
            <RowTitle><PeekName person={p} className="p-name__link" /></RowTitle>
            {p.organization && <RowSub><small>{p.organization.name}</small></RowSub>}
            {office && (p.ourAlderperson || p.tags?.length || p.notes) ? (
              <RowMeta className="p-tags">
                {p.ourAlderperson && <span className="p-tag p-tag--person">Our alderperson</span>}
                {p.tags?.map((t) => <span key={t.id} className={`p-tag p-tag--${TAG_TONE[t.tone]}`}>{t.label}</span>)}
                {!!p.notes && <span className="p-notes"><StickyNote size={11} aria-hidden />{p.notes}</span>}
              </RowMeta>
            ) : null}
          </span>
        </span>
      </RowMain>
      <RowCell width={110} className="p-cell p-cell--type"><span role="cell"><TypeChip type={p.type} /></span></RowCell>
      <RowCell width={56} hide="phone" className="p-cell p-cell--ward"><span role="cell">{p.type === "lobbyist" || p.type === "org_rep" || !p.ward ? "—" : p.ward}</span></RowCell>
      <RowCell width={96} className="p-cell p-cell--apps"><span role="cell">{p.appearances}</span></RowCell>
      <RowCell width={230} hide="phone" className="p-cell p-cell--areas"><span role="cell" className="p-cell__in">{p.areas.map((a) => <AreaChip key={a}>{a}</AreaChip>)}</span></RowCell>
      <RowCell width={170} hide="phone" className="p-cell p-cell--lean"><span role="cell">{p.leaning && <LeaningMeter estimate={p.leaning} size="mini" />}</span></RowCell>
      <RowCell width={120} hide="phone" className="p-cell p-cell--last"><span role="cell" className="p-cell__in">
        {p.lastSpoke && <><span>{mdy(p.lastSpoke.date)}</span><Watch meetingId={p.lastSpoke.meetingId} at={p.lastSpoke.at} /></>}
      </span></RowCell>
    </ListRow>
  );
});

export function PeopleTable({ office = false }: { office?: boolean }) {
  const order = usePeople((s) => s.order);
  const q = usePeople((s) => s.query.q.trim());
  return (<>
    <List as="div" variant="plain" className="p-table" label="People" role="table">
      <ListRow as="div" className="p-row p-row--head" role="row">
        <RowMain min={180} className="p-cell p-cell--person"><span role="columnheader">PERSON</span></RowMain>
        <RowCell width={110} className="p-cell p-cell--type"><span role="columnheader">TYPE</span></RowCell>
        <RowCell width={56} hide="phone" className="p-cell p-cell--ward"><span role="columnheader">WARD</span></RowCell>
        <RowCell width={96} className="p-cell p-cell--apps"><span role="columnheader">APPEARANCES</span></RowCell>
        <RowCell width={230} hide="phone" className="p-cell p-cell--areas"><span role="columnheader">POLICY AREAS</span></RowCell>
        <RowCell width={170} hide="phone" className="p-cell p-cell--lean"><span role="columnheader">LEANING · AI ESTIMATE <Info size={11} aria-hidden /></span></RowCell>
        <RowCell width={120} hide="phone" className="p-cell p-cell--last"><span role="columnheader">LAST SPOKE</span></RowCell>
      </ListRow>
      {order.map((id, i) => <Row key={id} id={id} index={i} office={office} />)}
    </List>
    {q && order.length === 0 && <NoResults query={q} />}
  </>);
}

export function TableFooter({ label }: { label: string }) {
  const { shown, total, loading } = usePeople(useShallow((s) => ({ shown: s.order.length, total: s.total, loading: s.loading })));
  return (
    <div className="p-footer">
      <span>{label.replace("{shown}", shown.toLocaleString("en-US")).replace("{total}", total.toLocaleString("en-US"))}</span>
      {shown < total && <button className="btn" disabled={loading} onClick={() => peopleStore.getState().loadMore()}><ChevronDown size={14} aria-hidden />Load more</button>}
    </div>
  );
}

/** The selection bar: Compare for two people (everyone), Tag + Export CSV for offices (screenshots/24 cards 2, 9). */
export function SelectionBar({ canTag, canExport }: { canTag: boolean; canExport: boolean }) {
  const selected = usePeople((s) => s.selected);
  const [tagging, setTagging] = useState(false);
  const [label, setLabel] = useState("");
  if (!selected.length) return null;
  const exportCsv = () => { location.href = `/api/dashboard/people/export.csv?ids=${selected.map(encodeURIComponent).join(",")}`; };
  const tag = async () => {
    if (!label.trim()) return;
    await Promise.all(selected.map((id) => peopleStore.getState().addTag(id, label.trim(), "neutral").catch(() => {})));
    setLabel(""); setTagging(false);
  };
  return (
    <>
      <div className="p-selbar" role="region" aria-label="Selection">
        <b>{selected.length} selected</b>
        <span className="spacer" />
        {selected.length === 2 && <a href={compareHref(selected[0], selected[1])}>Compare</a>}
        {canTag && (tagging
          ? <form onSubmit={(e) => { e.preventDefault(); tag(); }}><input autoFocus aria-label="Tag" value={label} onChange={(e) => setLabel(e.target.value)} /></form>
          : <button onClick={() => setTagging(true)}><Tag size={13} aria-hidden />Tag</button>)}
        {canExport && <button onClick={exportCsv}><Download size={13} aria-hidden />Export CSV</button>}
        <button aria-label="Clear selection" onClick={() => peopleStore.getState().clearSelection()}><X size={14} aria-hidden /></button>
      </div>
    </>
  );
}

export { PAGE };
